import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, WS_URL } from '@/App';
import { ArrowLeft, Clock, Check, Truck, Store, Package, XCircle, MessageCircle, Copy, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const statusConfig = {
  new: { label: 'Novo', icon: Clock, color: 'bg-blue-500/20 text-blue-400', step: 1 },
  confirmed: { label: 'Confirmado', icon: Check, color: 'bg-yellow-500/20 text-yellow-400', step: 2 },
  preparing: { label: 'Em Preparo', icon: Package, color: 'bg-purple-500/20 text-purple-400', step: 3 },
  out_for_delivery: { label: 'Saiu para Entrega', icon: Truck, color: 'bg-cyan-500/20 text-cyan-400', step: 4 },
  ready_for_pickup: { label: 'Pronto para Retirada', icon: Store, color: 'bg-cyan-500/20 text-cyan-400', step: 4 },
  completed: { label: 'Concluído', icon: Check, color: 'bg-green-500/20 text-green-400', step: 5 },
  cancelled: { label: 'Cancelado', icon: XCircle, color: 'bg-red-500/20 text-red-400', step: 0 }
};

const OrderStatus = () => {
  const { slug, orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!store?.id || !orderId) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsUrl = WS_URL || API.replace('http', 'ws');
    const ws = new WebSocket(`${wsUrl.replace('/api', '')}/ws/notifications/${store.id}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected for order status');
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('WebSocket message:', data);
        
        // Check if this update is for our order
        if (data.type === 'order_update' && data.order?.id === orderId) {
          setOrder(prev => ({
            ...prev,
            ...data.order,
            status: data.order.status
          }));
          
          // Show toast notification for status change
          const statusLabel = statusConfig[data.order.status]?.label || data.order.status;
          toast.success(`Pedido atualizado: ${statusLabel}`, {
            duration: 4000
          });
        }
        
        // Also check for status_update type
        if (data.type === 'status_update' && data.order_id === orderId) {
          setOrder(prev => ({
            ...prev,
            status: data.status
          }));
          
          const statusLabel = statusConfig[data.status]?.label || data.status;
          toast.success(`Pedido atualizado: ${statusLabel}`, {
            duration: 4000
          });
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      
      // Reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('Attempting to reconnect WebSocket...');
        connectWebSocket();
      }, 5000);
    };
    
    wsRef.current = ws;
  }, [store?.id, orderId]);

  // Fetch order and store data
  const fetchOrder = async () => {
    try {
      const [orderRes, storeRes] = await Promise.all([
        axios.get(`${API}/public/stores/${slug}/orders/${orderId}`),
        axios.get(`${API}/public/stores/${slug}`)
      ]);
      setOrder(orderRes.data);
      setStore(storeRes.data);
    } catch (e) {
      toast.error('Pedido não encontrado');
      navigate(`/${slug}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [slug, orderId]);

  // Connect WebSocket when store is loaded
  useEffect(() => {
    if (store?.id) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [store?.id, connectWebSocket]);

  // Fallback polling every 30s if WebSocket is not connected
  useEffect(() => {
    if (!wsConnected) {
      const interval = setInterval(fetchOrder, 15000); // Poll every 15s as fallback
      return () => clearInterval(interval);
    }
  }, [wsConnected, slug, orderId]);

  const copyOrderLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copiado!');
  };

  // Generate WhatsApp message for resending
  const sendWhatsAppMessage = () => {
    if (!store?.whatsapp || !order) return;
    
    let msg = `🛒 *PEDIDO #${order.order_number}*\n\n`;
    msg += `👤 *Cliente:* ${order.customer_name}\n`;
    msg += `📱 *Telefone:* ${order.customer_phone}\n\n`;
    
    if (order.delivery_type === 'delivery' && order.address) {
      msg += `📍 *Endereço:*\n`;
      msg += `${order.address.street}, ${order.address.number}\n`;
      if (order.address.complement) msg += `${order.address.complement}\n`;
      msg += `${order.address.neighborhood}`;
      if (order.address.city) msg += ` - ${order.address.city}`;
      msg += `\n`;
      if (order.address.reference) msg += `Ref: ${order.address.reference}\n`;
      msg += `\n`;
    } else {
      msg += `🏪 *Retirada no local*\n\n`;
    }
    
    msg += `📦 *Itens:*\n`;
    order.items.forEach(item => {
      msg += `• ${item.quantity}x ${item.product_name}`;
      if (item.variant_name) msg += ` (${item.variant_name})`;
      msg += ` - R$ ${(item.unit_price * item.quantity).toFixed(2)}\n`;
    });
    
    msg += `\n💰 *Subtotal:* R$ ${order.subtotal.toFixed(2)}\n`;
    if (order.delivery_fee > 0) msg += `🚚 *Taxa de entrega:* R$ ${order.delivery_fee.toFixed(2)}\n`;
    if (order.discount > 0) msg += `🎫 *Desconto:* -R$ ${order.discount.toFixed(2)}\n`;
    msg += `*TOTAL: R$ ${order.total.toFixed(2)}*\n\n`;
    
    const paymentLabels = { pix: 'Pix', cash: 'Dinheiro', card: 'Cartão' };
    msg += `💳 *Pagamento:* ${paymentLabels[order.payment_method] || order.payment_method}\n`;
    
    if (order.notes) msg += `\n📝 *Observações:* ${order.notes}\n`;
    
    const whatsappNumber = store.whatsapp.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/55${whatsappNumber}?text=${encodeURIComponent(msg)}`;
    
    const whatsappWindow = window.open(whatsappUrl, '_blank');
    
    if (!whatsappWindow || whatsappWindow.closed) {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(msg.replace(/\*/g, ''));
      toast.info(
        <div>
          <p className="font-bold">Pop-up bloqueado!</p>
          <p className="text-sm">Mensagem copiada! Cole no WhatsApp.</p>
          <a 
            href={whatsappUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-green-400 underline text-sm mt-2 block"
          >
            Ou clique aqui para abrir
          </a>
        </div>,
        { duration: 8000 }
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status];
  const StatusIcon = status?.icon || Clock;
  const isDelivery = order.delivery_type === 'delivery';

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      <header className="sticky top-0 z-50 bg-[#0a0a0f] border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => navigate(`/${slug}`)}
                className="text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-bold text-white">Pedido #{order.order_number}</h1>
                  {wsConnected ? (
                    <Wifi className="w-4 h-4 text-green-400" title="Atualizações em tempo real ativas" />
                  ) : (
                    <WifiOff className="w-4 h-4 text-yellow-400" title="Reconectando..." />
                  )}
                </div>
                <span className="text-xs text-white/60 font-mono">{order.id.slice(0, 8)}</span>
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={copyOrderLink}
              className="text-white"
            >
              <Copy className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Status Card */}
        <div className="card-adegga p-6 text-center">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${status.color} mb-4`}>
            <StatusIcon className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{status.label}</h2>
          {order.status === 'cancelled' && order.cancel_reason && (
            <p className="text-red-400 text-sm">{order.cancel_reason}</p>
          )}
          
          {/* Progress Steps */}
          {order.status !== 'cancelled' && (
            <div className="flex justify-center items-center gap-2 mt-6">
              {[1, 2, 3, 4, 5].map(step => (
                <React.Fragment key={step}>
                  <div className={`w-3 h-3 rounded-full ${
                    step <= status.step ? 'bg-[#ff6b00]' : 'bg-white/20'
                  }`} />
                  {step < 5 && (
                    <div className={`w-8 h-0.5 ${
                      step < status.step ? 'bg-[#ff6b00]' : 'bg-white/20'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Store Contact */}
        {store?.whatsapp && (
          <div className="space-y-2">
            <button
              onClick={sendWhatsAppMessage}
              className="card-adegga p-4 flex items-center justify-between w-full hover:bg-green-500/10 transition-colors"
            >
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">Enviar Pedido via WhatsApp</span>
              </div>
              <span className="text-green-400 text-sm">Clique aqui</span>
            </button>
            <a
              href={`https://wa.me/55${store.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="card-adegga p-4 flex items-center justify-between block"
            >
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-white/60" />
                <span className="text-white/60">Falar com a loja</span>
              </div>
              <span className="text-white/60">{store.whatsapp}</span>
            </a>
          </div>
        )}

        {/* Order Details */}
        <div className="card-adegga p-4 space-y-4">
          <h3 className="font-bold text-white">Detalhes do Pedido</h3>
          
          <div className="space-y-3">
            {order.items.map((item, i) => (
              <div key={i} className="flex justify-between">
                <div>
                  <span className="text-white">{item.quantity}x {item.product_name}</span>
                  {item.variant_name && (
                    <span className="text-white/60 text-sm"> ({item.variant_name})</span>
                  )}
                  {item.addons?.length > 0 && (
                    <p className="text-xs text-white/50">
                      {item.addons.map(a => `+ ${a.quantity}x ${a.name}`).join(', ')}
                    </p>
                  )}
                </div>
                <span className="text-white">R$ {(item.unit_price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          
          <div className="border-t border-white/10 pt-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Subtotal</span>
              <span className="text-white">R$ {order.subtotal.toFixed(2)}</span>
            </div>
            {order.delivery_fee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Taxa de entrega</span>
                <span className="text-white">R$ {order.delivery_fee.toFixed(2)}</span>
              </div>
            )}
            {order.discount > 0 && (
              <div className="flex justify-between text-sm text-green-400">
                <span>Desconto</span>
                <span>-R$ {order.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2">
              <span className="text-white">Total</span>
              <span className="text-[#ff6b00]">R$ {order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        <div className="card-adegga p-4 space-y-3">
          <h3 className="font-bold text-white">Informações de Entrega</h3>
          
          <div className="flex items-center gap-2">
            {isDelivery ? <Truck className="w-5 h-5 text-[#ff6b00]" /> : <Store className="w-5 h-5 text-[#ff6b00]" />}
            <span className="text-white">{isDelivery ? 'Entrega' : 'Retirada no local'}</span>
          </div>
          
          {isDelivery && order.address && (
            <div className="text-white/60 text-sm pl-7">
              <p>{order.address.street}, {order.address.number}</p>
              {order.address.complement && <p>{order.address.complement}</p>}
              <p>{order.address.neighborhood} - {order.address.city}</p>
              {order.address.reference && <p className="italic">Ref: {order.address.reference}</p>}
            </div>
          )}
          
          <div className="pt-2 border-t border-white/10">
            <p className="text-sm text-white/60">
              <span className="font-medium text-white">Pagamento:</span>{' '}
              {order.payment_method === 'pix' ? 'Pix' : order.payment_method === 'cash' ? 'Dinheiro' : 'Cartão'}
              {order.payment_method === 'cash' && order.change_for && (
                <span> (Troco para R$ {order.change_for})</span>
              )}
            </p>
          </div>
        </div>

        {/* Pix Info */}
        {order.payment_method === 'pix' && store?.pix_config?.key && order.status !== 'completed' && order.status !== 'cancelled' && (
          <div className="card-adegga p-4 bg-[#ff6b00]/10 border-[#ff6b00]/30">
            <h3 className="font-bold text-white mb-3">Pagamento via Pix</h3>
            <p className="text-sm text-white/60 mb-2">Chave Pix:</p>
            <p className="text-[#ff6b00] font-mono break-all text-lg">{store.pix_config.key}</p>
            {store.pix_config.holder_name && (
              <p className="text-sm text-white/60 mt-2">{store.pix_config.holder_name}</p>
            )}
            <p className="text-2xl font-bold text-white mt-4">R$ {order.total.toFixed(2)}</p>
          </div>
        )}

        {/* Back to Store */}
        <Link to={`/${slug}`}>
          <Button className="w-full btn-secondary">
            Voltar para a loja
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default OrderStatus;
